import { ArrowRight, HeartPulse, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useHealthSummary, useTelemetryHealth } from './hooks';

function pluralize(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function getFreshnessLabel(freshnessSec?: number) {
  if (typeof freshnessSec !== 'number') return 'Freshness unavailable';
  if (!Number.isFinite(freshnessSec)) return 'Telemetry degraded';
  return `Freshness ${freshnessSec}s`;
}

export default function HealthBanner({ enabled = true }: { enabled?: boolean }) {
  const summaryQuery = useHealthSummary(enabled);
  const telemetryQuery = useTelemetryHealth(enabled);
  const summary = summaryQuery.data;
  const freshnessSec = telemetryQuery.data?.freshnessSec;
  const telemetryDegraded = telemetryQuery.isError || (typeof freshnessSec === 'number' && !Number.isFinite(freshnessSec));

  const tone = !summary
    ? 'slate'
    : summary.down > 0 || summary.status === 'Critical'
      ? 'red'
      : summary.degraded > 0 || summary.status === 'Degraded' || telemetryDegraded
        ? 'amber'
        : summary.status === 'Healthy'
          ? 'green'
          : 'slate';

  const classes = tone === 'green'
    ? {
        wrapper: 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100/70',
        icon: 'bg-emerald-600 text-white',
        badge: 'bg-white/80 text-emerald-700 border border-emerald-200',
      }
    : tone === 'amber'
      ? {
          wrapper: 'border-amber-200 bg-amber-50 hover:bg-amber-100/70',
          icon: 'bg-amber-500 text-white',
          badge: 'bg-white/80 text-amber-700 border border-amber-200',
        }
      : tone === 'red'
        ? {
            wrapper: 'border-red-200 bg-red-50 hover:bg-red-100/70',
            icon: 'bg-red-600 text-white',
            badge: 'bg-white/80 text-red-700 border border-red-200',
          }
        : {
            wrapper: 'border-slate-200 bg-white hover:bg-slate-50',
            icon: 'bg-slate-700 text-white',
            badge: 'bg-slate-100 text-slate-600 border border-slate-200',
          };

  const summaryText = !summary
    ? summaryQuery.isLoading
      ? 'Loading platform health...'
      : 'Platform health is unavailable'
    : summary.down > 0
      ? `Platform incident - ${pluralize(summary.down, 'service')} down`
      : summary.degraded > 0
        ? `Platform degraded - ${pluralize(summary.degraded, 'service')} degraded`
        : summary.total > 0 && summary.status === 'Healthy'
          ? `Platform healthy - ${summary.ok}/${summary.total} services up`
          : summary.label;

  const detailText = summary
    ? `Last update ${summary.updatedAt ? new Date(summary.updatedAt).toLocaleTimeString() : 'recently'}`
    : 'Open System Health for the detailed breakdown';

  const StatusIcon = tone === 'red' ? ShieldAlert : tone === 'green' ? ShieldCheck : HeartPulse;

  return (
    <Link
      to="/system"
      className={`block rounded-2xl border px-5 py-4 transition-colors ${classes.wrapper}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${classes.icon}`}>
            <StatusIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold text-slate-900">{summaryText}</div>
            <div className="text-sm text-slate-600">{detailText}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center">
          {summary?.platform_build && (
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${classes.badge}`}
              title="Deploy-injected PLATFORM_BUILD from the running orchestrator"
            >
              {summary.platform_build}
            </span>
          )}
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${classes.badge}`}>
            {telemetryQuery.isLoading ? 'Loading freshness...' : getFreshnessLabel(freshnessSec)}
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-700">
            View system
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
